import type { CloudConstructKind, Filters } from '@ipfabric/utils';
import type { DeviceViewNodeEntity } from 'components/DeviceView/deviceViewTypes';
import { getCloudConstructKindFromNode } from 'components/DeviceView/helpers/cloudConstruct';

/**
 * Hidden cloud-construct filters for the device explorer (NIM-24243 AC2).
 * Equivalent of the on-prem `sn`-eq filter, but the column key and the
 * value source vary per (constructKind, apiEndpoint).
 */
type ExtraValueSource =
  | 'extraCloudNetworkId'
  | 'extraRemoteCloudNetworkId'
  | 'extraSubnetId'
  | 'extraRouteTableId'
  | 'extraName';

type ValueSource = 'sn' | 'vendorId' | ExtraValueSource;

interface CloudFilterSpec {
  column: string;
  source: ValueSource;
  /**
   * When the target column is a text-array (e.g. a VM's set of cloud network
   * ids, one per interface), match with the array `any eq` operator instead of
   * a scalar `eq`.
   */
  arrayAny?: boolean;
}

/**
 * A filter for one sub-table. Either:
 * - a single spec (or array of specs) which are ANDed together, or
 * - an `anyOf` group: the row matches ANY of several branches (OR). Each branch
 *   is itself one spec or an array of specs ANDed together. Used to show "both
 *   sides" of a peering: the two networks (each branch a single spec), or the
 *   two directional peering rows (each branch a `cloudNetworkId AND
 *   remoteNetworkId` pair).
 */
type CloudFilterBranch = CloudFilterSpec | CloudFilterSpec[];
interface CloudFilterAnyOf {
  anyOf: CloudFilterBranch[];
}
type CloudFilterEntry = CloudFilterSpec | CloudFilterSpec[] | CloudFilterAnyOf;

type CloudConstructFilters = {
  [Kind in CloudConstructKind]?: {
    [apiEndpoint: string]: CloudFilterEntry;
  };
};

const isAnyOf = (entry: CloudFilterEntry): entry is CloudFilterAnyOf =>
  !Array.isArray(entry) && 'anyOf' in entry;

// Spec mapping each (constructKind, apiEndpoint) -> filter column + value source.
const cloudConstructFilters: CloudConstructFilters = {
  cloudNetwork: {
    '/tables/cloud/subnets/inventory': {
      column: 'cloudNetworkId',
      source: 'sn',
    },
    '/tables/cloud/route-tables/inventory': {
      column: 'cloudNetworkId',
      source: 'sn',
    },
    '/tables/cloud/endpoints/virtual-machines': {
      column: 'cloudNetworkIds',
      source: 'sn',
      // A VM can have interfaces in several networks; cloudNetworkIds is an
      // array column, so match with `any eq`.
      arrayAny: true,
    },
    '/tables/cloud/network-interfaces': {
      column: 'cloudNetworkId',
      source: 'sn',
    },
  },
  networkInterface: {
    '/tables/cloud/subnets/inventory': {
      column: 'subnetId',
      source: 'extraSubnetId',
    },
    '/tables/cloud/networks/inventory': {
      column: 'networkId',
      source: 'extraCloudNetworkId',
    },
    // Route tables attached to the interface's subnet (route-tables carry the
    // set of subnet ids they apply to); lists both effective and custom RTs.
    '/tables/cloud/route-tables/inventory': {
      column: 'subnetIds',
      source: 'extraSubnetId',
      arrayAny: true,
    },
  },
  subnet: {
    '/tables/cloud/networks/inventory': {
      column: 'networkId',
      source: 'extraCloudNetworkId',
    },
    '/tables/cloud/network-interfaces': {
      column: 'subnetId',
      source: 'sn',
    },
    // Route tables attached to this subnet (see note above); lists both the
    // effective and any custom route tables.
    '/tables/cloud/route-tables/inventory': {
      column: 'subnetIds',
      source: 'sn',
      arrayAny: true,
    },
  },
  peering: {
    // Show exactly the two directional rows of THIS peering pair — the (local,
    // remote) pairing and its reverse — not every peering that merely touches
    // one of the two networks. Each branch pins BOTH endpoints.
    '/tables/cloud/networks/peerings': {
      anyOf: [
        [
          { column: 'cloudNetworkId', source: 'extraCloudNetworkId' },
          { column: 'remoteNetworkId', source: 'extraRemoteCloudNetworkId' },
        ],
        [
          { column: 'cloudNetworkId', source: 'extraRemoteCloudNetworkId' },
          { column: 'remoteNetworkId', source: 'extraCloudNetworkId' },
        ],
      ],
    },
    // Show both networks involved in the peering.
    '/tables/cloud/networks/inventory': {
      anyOf: [
        { column: 'networkId', source: 'extraCloudNetworkId' },
        { column: 'networkId', source: 'extraRemoteCloudNetworkId' },
      ],
    },
  },
  instance: {
    // `sn` is the VM id (see `assertHostDevice` retyping, NIM-24386).
    '/tables/cloud/endpoints/virtual-machines': {
      column: 'vmId',
      source: 'sn',
    },
    '/tables/cloud/network-interfaces': {
      column: 'vmId',
      source: 'sn',
    },
  },
  // NAT gateway (NIM-25330). `sn` is the gateway id (vCloudNatGateways.id).
  natGateway: {
    // This gateway's own rules; the rules table exposes the parent as gatewayId.
    '/tables/cloud/nat-gateways/rules': {
      column: 'gatewayId',
      source: 'sn',
    },
    '/tables/cloud/networks/inventory': {
      column: 'networkId',
      source: 'extraCloudNetworkId',
    },
  },
  // Internet gateway (NIM-25330). `sn` is the gateway id
  // (vCloudInternetGateways.id). AWS-only — see cloudInternetGwNodeTypes.
  internetGateway: {
    '/tables/cloud/networks/inventory': {
      column: 'networkId',
      source: 'extraCloudNetworkId',
    },
  },
};

const extraKeyBySource: Record<ExtraValueSource, string> = {
  extraCloudNetworkId: 'cloudNetworkId',
  extraRemoteCloudNetworkId: 'remoteCloudNetworkId',
  extraRouteTableId: 'routeTableId',
  extraSubnetId: 'subnetId',
  extraName: 'cloudConstructName',
};

const resolveEntitySource = (
  entity: DeviceViewNodeEntity,
  source: ValueSource,
): string | undefined => {
  if (source === 'sn') {
    return entity.sn || undefined;
  }
  if (source === 'vendorId') {
    return entity.extra?.vendor === 'gcp' ? undefined : entity.sn || undefined;
  }
  const value = entity.extra?.[extraKeyBySource[source]];
  return typeof value === 'string' && value !== '' ? value : undefined;
};

/**
 * Returns the cloud-construct hidden filter for the given entity + api
 * endpoint, or `null` when the entity is not a cloud construct or no mapping
 * exists for that (constructKind, apiEndpoint) pair.
 */
export const getCloudConstructFilters = (
  deviceViewEntity: DeviceViewNodeEntity,
  apiEndpoint: string,
): Filters | null => {
  const kind = getCloudConstructKindFromNode(
    deviceViewEntity.nodeType,
    deviceViewEntity.extra,
  );
  if (kind === null) {
    return null;
  }
  const entry = cloudConstructFilters[kind]?.[apiEndpoint];
  if (entry === undefined) {
    return null;
  }

  const toCondition = (spec: CloudFilterSpec): Filters | null => {
    const value = resolveEntitySource(deviceViewEntity, spec.source);
    if (value === undefined) {
      return null;
    }
    return {
      [spec.column]: spec.arrayAny ? ['any', 'eq', value] : ['eq', value],
    };
  };

  // AND a branch's specs together. Returns null if ANY spec can't resolve, so a
  // paired branch (e.g. cloudNetworkId AND remoteNetworkId) is only kept when
  // both endpoints are known.
  const andOf = (branch: CloudFilterBranch): Filters | null => {
    const specs = Array.isArray(branch) ? branch : [branch];
    const conditions = specs.map(toCondition);
    if (conditions.some((c) => c === null)) {
      return null;
    }
    const resolved = conditions as Filters[];
    if (resolved.length === 0) {
      return null;
    }
    return resolved.length === 1 ? (resolved[0] ?? null) : { and: resolved };
  };

  // `anyOf` -> the row matches ANY branch (OR); each branch is ANDed. Used to
  // show both sides of a peering (both networks, or both directional rows).
  if (isAnyOf(entry)) {
    const branches = entry.anyOf
      .map(andOf)
      .filter((c): c is Filters => c !== null);
    if (branches.length === 0) {
      return null;
    }
    return branches.length === 1 ? (branches[0] ?? null) : { or: branches };
  }

  return andOf(entry);
};

export const hasCloudConstructFilter = (
  deviceViewEntity: DeviceViewNodeEntity,
  apiEndpoint: string,
): boolean => {
  const kind = getCloudConstructKindFromNode(
    deviceViewEntity.nodeType,
    deviceViewEntity.extra,
  );
  return (
    kind !== null && cloudConstructFilters[kind]?.[apiEndpoint] !== undefined
  );
};
